import React, { useState } from 'react';
import { Bell, X, Check, Trash2, Wifi, WifiOff } from 'lucide-react';
import { useNotifications, Notification } from '@/hooks/useNotifications';

import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const NotificationBell: React.FC = () => {
  const {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    handleNotificationAction,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'URGENT':
        return '🚨';
      case 'SUCCESS':
        return '✅';
      case 'WARNING':
        return '⚠️';
      case 'ERROR':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Marcar como lida se não estiver lida
    if (!notification.readAt) {
      markAsRead(notification.id);
    }
    
    // Abrir modal com detalhes da notificação
    setSelectedNotification(notification);
    setIsModalOpen(true);
    
    // Fechar dropdown
    setIsOpen(false);
  };

  const handleModalClose = () => {
    const notification = selectedNotification;
    setIsModalOpen(false);
    setSelectedNotification(null);
    
    // Processar ação da notificação após fechar o modal (se houver actionUrl)
    if (notification?.actionUrl) {
      handleNotificationAction(notification);
    }
  };

  return (
    <div className="relative">
      {/* Botão do sino */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:bg-slate-700 rounded-lg transition-colors"
      >
        <Bell className="w-5 h-5" />
        
        {/* Badge de notificações não lidas */}
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
        
        {/* Indicador de conexão */}
        <div className="absolute -bottom-1 -right-1">
          {isConnected ? (
            <Wifi className="w-3 h-3 text-green-500" />
          ) : (
            <WifiOff className="w-3 h-3 text-red-500" />
          )}
        </div>
      </button>

      {/* Dropdown de notificações */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-2xl dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700 z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">Notificações</h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markAllAsRead()}
                    className="text-xs"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Marcar todas como lidas
                  </Button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-slate-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Lista de notificações */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500 dark:text-slate-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-sm">Carregando...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma notificação</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 dark:bg-slate-800 cursor-pointer transition-colors ${
                      !notification.readAt ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Ícone */}
                      <div className="flex-shrink-0">
                        <span className="text-lg">
                          {getNotificationIcon(notification.type)}
                        </span>
                      </div>

                      {/* Conteúdo */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                            {notification.title}
                          </h4>
                          <div className="flex items-center space-x-1">
                            {!notification.readAt && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="text-gray-400 hover:text-red-500 p-1"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 dark:text-slate-300 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(notification.createdAt), {
                              addSuffix: true,
                              locale: ptBR,
                            })}
                          </span>
                          
                          {notification.priority === 'URGENT' && (
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              Urgente
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
                <span>{unreadCount} não lidas</span>
                <span>{notifications.length} total</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de detalhes da notificação */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={selectedNotification?.title || 'Notificação'}
      >
        {selectedNotification && (
          <div className="space-y-4">
            {/* Ícone e tipo */}
            <div className="flex items-center space-x-3">
              <span className="text-3xl">
                {getNotificationIcon(selectedNotification.type)}
              </span>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Badge
                    className={
                      selectedNotification.type === 'URGENT'
                        ? 'bg-red-100 text-red-800'
                        : selectedNotification.type === 'ERROR'
                        ? 'bg-red-100 text-red-800'
                        : selectedNotification.type === 'WARNING'
                        ? 'bg-yellow-100 text-yellow-800'
                        : selectedNotification.type === 'SUCCESS'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }
                  >
                    {selectedNotification.type}
                  </Badge>
                  {selectedNotification.priority === 'URGENT' && (
                    <Badge className="bg-red-500 text-white">Urgente</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Mensagem */}
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
              <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
                {selectedNotification.message}
              </p>
            </div>

            {/* Informações adicionais */}
            <div className="space-y-2 text-sm text-gray-600 dark:text-slate-400">
              <div className="flex items-center justify-between">
                <span>Data:</span>
                <span>
                  {format(new Date(selectedNotification.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Há:</span>
                <span>
                  {formatDistanceToNow(new Date(selectedNotification.createdAt), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </span>
              </div>
              {selectedNotification.readAt && (
                <div className="flex items-center justify-between">
                  <span>Lida em:</span>
                  <span>
                    {format(new Date(selectedNotification.readAt), "dd/MM/yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Botões de ação */}
            <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200 dark:border-slate-700">
              {selectedNotification.actionUrl && (
                <Button
                  onClick={() => {
                    handleNotificationAction(selectedNotification);
                    handleModalClose();
                  }}
                  variant="default"
                >
                  Ver Detalhes
                </Button>
              )}
              <Button
                onClick={handleModalClose}
                variant="outline"
              >
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
